<script setup lang="ts">
  import { Plus, Trash2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
  import type { AdTemplateFont, AvailableFont } from "@/types";

  const props = defineProps<{ available: AvailableFont[] }>();
  const fonts = defineModel<AdTemplateFont[]>({ required: true });

  const weightsOf = (family: string) => props.available.find((font) => font.family === family)?.weights ?? [];

  const setFamily = (index: number, family: string) => {
    const weights = weightsOf(family);
    const current = fonts.value[index]?.weight ?? 400;
    const weight = weights.includes(current) ? current : (weights[0] ?? 400);
    fonts.value = fonts.value.map((font, other) => (other === index ? { family, weight } : font));
  };

  const setWeight = (index: number, weight: number) => {
    fonts.value = fonts.value.map((font, other) => (other === index ? { ...font, weight } : font));
  };

  const remove = (index: number) => {
    fonts.value = fonts.value.filter((_, other) => other !== index);
  };

  const add = () => {
    const family = props.available[0]?.family ?? "Inter";
    fonts.value = [...fonts.value, { family, weight: weightsOf(family).includes(400) ? 400 : (weightsOf(family)[0] ?? 400) }];
  };
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-xs text-muted-foreground">
      The renderer inlines these fonts in the template. Use them in the CSS with
      <code class="font-mono">font-family</code>
      and
      <code class="font-mono">font-weight</code>
      . A font change shows in the preview once the draft is saved.
    </p>
    <div
      v-for="(font, index) in fonts"
      :key="`${font.family}-${font.weight}-${index}`"
      class="flex items-center gap-2">
      <Select
        :model-value="font.family"
        @update:model-value="setFamily(index, String($event))">
        <SelectTrigger class="w-48 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="option in available"
            :key="option.family"
            :value="option.family">
            {{ option.family }}
          </SelectItem>
        </SelectContent>
      </Select>
      <Select
        :model-value="String(font.weight)"
        @update:model-value="setWeight(index, Number($event))">
        <SelectTrigger class="w-28 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            v-for="weight in weightsOf(font.family)"
            :key="weight"
            :value="String(weight)">
            {{ weight }}
          </SelectItem>
        </SelectContent>
      </Select>
      <Button
        size="icon"
        variant="ghost"
        @click="remove(index)">
        <Trash2 class="size-4" />
      </Button>
    </div>
    <Button
      size="sm"
      variant="outline"
      class="self-start"
      @click="add">
      <Plus class="size-4" />
      Add font
    </Button>
  </div>
</template>
